<script setup>
import { computed, ref, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { debounce } from '@chatwoot/utils';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import LinkedEntitySection from 'dashboard/components-next/LinkedEntitySection.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';

const props = defineProps({
  company: {
    type: Object,
    default: () => ({}),
  },
  contacts: {
    type: Array,
    default: () => [],
  },
  meta: {
    type: Object,
    default: () => ({}),
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  isBusy: {
    type: Boolean,
    default: false,
  },
  searchResults: {
    type: Array,
    default: () => [],
  },
  isSearching: {
    type: Boolean,
    default: false,
  },
  selectedContact: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits([
  'cancelContactSelection',
  'confirmContactSelection',
  'removeContact',
  'search',
  'selectContact',
  'update:currentPage',
]);

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const searchQuery = ref('');

const hasContacts = computed(() => props.contacts.length > 0);
const currentPage = computed(() => Number(props.meta?.page || 1));
const totalContacts = computed(() => Number(props.meta?.totalCount || 0));
const linkedContactIds = computed(
  () => new Set(props.contacts.map(contact => Number(contact.id)))
);
const showPaginationFooter = computed(
  () => hasContacts.value && totalContacts.value > props.contacts.length
);

// Dentro de uma pagina com LinkedEntityPane, abre o contato na propria coluna.
const openLinkedInPlace = inject('openLinkedInPlace', null);

const openContact = contactId => {
  if (openLinkedInPlace) {
    openLinkedInPlace('contact', contactId);
    return;
  }
  router.push({
    name: 'contacts_edit',
    params: { accountId: route.params.accountId, contactId },
  });
};

const contactMeta = contact =>
  [contact.email, contact.phoneNumber].filter(Boolean).join(' • ');

const contactName = contact =>
  contact.name || t('COMPANIES.DETAIL.CONTACTS.UNNAMED_CONTACT');

const contactOptions = computed(() =>
  props.searchResults
    .filter(
      contact =>
        !contact.linkedToCurrentCompany &&
        !linkedContactIds.value.has(Number(contact.id))
    )
    .map(contact => ({
      value: contact.id,
      label: [contactName(contact), contact.email, contact.phoneNumber]
        .filter(Boolean)
        .join(' · '),
    }))
);

const emptyState = computed(() => {
  if (props.isSearching) return t('COMPANIES.DETAIL.CONTACTS.LOADING');
  return t('COMPANIES.DETAIL.CONTACTS.DIALOGS.ADD.EMPTY');
});

const selectedContactName = computed(() =>
  props.selectedContact ? contactName(props.selectedContact) : ''
);

const selectedContactMeta = computed(() =>
  props.selectedContact ? contactMeta(props.selectedContact) : ''
);

const selectedContactCompanyName = computed(
  () => props.selectedContact?.company?.name || ''
);

const summaryRows = computed(() => [
  {
    key: 'company',
    label: t('COMPANIES.DETAIL.CONTACTS.DIALOGS.ADD.COMPANY_LABEL'),
    avatarName: props.company.name || t('COMPANIES.UNNAMED'),
    avatarSrc: props.company.avatarUrl,
    primary: props.company.name || t('COMPANIES.UNNAMED'),
    secondary: props.company.domain,
  },
  {
    key: 'contact',
    label: t('COMPANIES.DETAIL.CONTACTS.DIALOGS.ADD.CONTACT_LABEL'),
    badge: selectedContactCompanyName.value
      ? t('COMPANIES.DETAIL.CONTACTS.DIALOGS.ADD.CURRENT_COMPANY', {
          companyName: selectedContactCompanyName.value,
        })
      : '',
    avatarName: selectedContactName.value,
    avatarSrc: props.selectedContact?.thumbnail,
    primary: selectedContactName.value,
    secondary: selectedContactMeta.value,
  },
]);

const debouncedSearch = debounce(query => {
  emit('search', query);
}, 300);

const handleSearch = query => {
  searchQuery.value = query;
  debouncedSearch(query.trim());
};

const handleContactSelect = contactId => {
  const selectedContact = props.searchResults.find(
    contact => contact.id === Number(contactId)
  );

  if (selectedContact) {
    emit('selectContact', selectedContact);
  }
};

const sectionLabels = computed(() => ({
  addTitle: t('COMPANIES.DETAIL.CONTACTS.ACTIONS.ADD'),
  addDescription: t('COMPANIES.DETAIL.CONTACTS.DIALOGS.ADD.DESCRIPTION'),
  searchPlaceholder: t(
    'COMPANIES.DETAIL.CONTACTS.DIALOGS.ADD.SEARCH_PLACEHOLDER'
  ),
  emptySearch: emptyState.value,
  confirmTitle: t('COMPANIES.DETAIL.CONTACTS.DIALOGS.ADD.CONFIRM_TITLE'),
  confirmDescription: t(
    'COMPANIES.DETAIL.CONTACTS.DIALOGS.ADD.CONFIRM_DESCRIPTION'
  ),
  confirm: t('COMPANIES.DETAIL.CONTACTS.DIALOGS.ADD.ADD'),
  cancel: t('COMPANIES.DETAIL.CONTACTS.DIALOGS.ADD.CANCEL'),
  sectionTitle: t('COMPANIES.DETAIL.SIDEBAR.TABS.CONTACTS'),
  loading: t('COMPANIES.DETAIL.CONTACTS.LOADING'),
  empty: t('COMPANIES.DETAIL.CONTACTS.EMPTY'),
  open: t('COMPANIES.DETAIL.CONTACTS.ACTIONS.OPEN'),
  remove: t('COMPANIES.DETAIL.CONTACTS.ACTIONS.REMOVE'),
}));

const items = computed(() =>
  props.contacts.map(contact => ({
    id: contact.id,
    name: contactName(contact),
    subtitle: contactMeta(contact),
    avatarUrl: contact.thumbnail,
  }))
);

const countLabel = computed(() =>
  t('COMPANIES.CONTACTS_COUNT', { n: totalContacts.value })
);
</script>

<template>
  <LinkedEntitySection
    :items="items"
    :candidate="selectedContact"
    :options="contactOptions"
    :labels="sectionLabels"
    :count-label="countLabel"
    :is-busy="isBusy"
    :is-loading="isLoading"
    @search="handleSearch"
    @select="handleContactSelect"
    @cancel="emit('cancelContactSelection')"
    @confirm="emit('confirmContactSelection')"
    @open="openContact"
    @remove="emit('removeContact', $event)"
  >
    <template #confirm>
      <div class="flex flex-col gap-4">
        <div
          v-for="row in summaryRows"
          :key="row.key"
          class="flex flex-col gap-2"
        >
          <div class="flex items-center justify-between h-5 gap-2">
            <label class="text-sm text-n-slate-12">
              {{ row.label }}
            </label>
            <span
              v-if="row.badge"
              class="px-2 py-0.5 text-xs rounded-md text-n-amber-11 bg-n-alpha-2"
            >
              {{ row.badge }}
            </span>
          </div>

          <div
            class="border border-n-strong h-[60px] gap-2 flex items-center rounded-xl p-3"
          >
            <Avatar
              :name="row.avatarName"
              :src="row.avatarSrc"
              :size="32"
              rounded-full
              hide-offline-status
            />
            <div class="flex flex-col w-full min-w-0 gap-1">
              <span
                class="text-sm leading-4 font-medium truncate text-n-slate-12"
              >
                {{ row.primary }}
              </span>
              <span
                v-if="row.secondary"
                class="text-sm leading-4 truncate text-n-slate-11"
              >
                {{ row.secondary }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <PaginationFooter
        v-if="showPaginationFooter"
        current-page-info="CONTACTS_LAYOUT.PAGINATION_FOOTER.SHOWING"
        :current-page="currentPage"
        :total-items="totalContacts"
        :items-per-page="15"
        class="!px-0 before:hidden bg-transparent"
        @update:current-page="emit('update:currentPage', $event)"
      />
    </template>
  </LinkedEntitySection>
</template>
